import {
    useEffect,
    useState
} from "react";
import {
    ActivityIndicator,
    ScrollView,
    Image,
    View
} from "react-native";
import type NotificationDetailModalProps from "./type";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme,
    Button,
    Modal,
    Text
} from "ncore-ui-kit";
import {
    type GetSentNotificationByIdPayload
} from "../../../../services/restAPI/actions/getSentNotificationById/type";
import moment from "moment";
import RESTServiceContext from "../../../../services/restAPI/context";

const NotificationDetailModal = ({
    notificationId,
    isVisible,
    onClose
}: NotificationDetailModalProps) => {
    const {
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const [
        detail,
        setDetail
    ] = useState<GetSentNotificationByIdPayload | null>(null);

    const [
        loading,
        setLoading
    ] = useState<boolean>(true);

    useEffect(() => {
        if(!isVisible || !notificationId) {
            setDetail(null);
            return;
        }

        const fetchDetail = async () => {
            try {
                setLoading(true);
                const response = await RESTServiceContext.action("getSentNotificationById", {
                    id: notificationId
                });

                if(response.payload) {
                    setDetail(response.payload);
                }
            } catch(error) {
                console.error(error);
            } finally {
                setLoading(false);
            }
        };

        fetchDetail();
    }, [
        notificationId,
        isVisible
    ]);

    const renderContent = () => {
        if(loading) {
            return <View
                style={[
                    stylesheet.loadingContainer,
                    {
                        padding: spaces.spacingXl
                    }
                ]}
            >
                <ActivityIndicator
                    color={colors.content.text.high}
                    size="large"
                />
            </View>;
        }

        if(!detail) {
            return <View
                style={[
                    stylesheet.emptyStateContainer,
                    {
                        padding: spaces.spacingLg
                    }
                ]}
            >
                <Text
                    customColor={colors.content.text.mid}
                    variant="bodyMediumSize"
                >
                    {localize("notifications-detail-load-error")}
                </Text>
            </View>;
        }

        const readRate = Math.min(100, Math.max(0, detail.readRate || 0));

        return <ScrollView
            style={stylesheet.contentContainer}
            showsVerticalScrollIndicator={true}
        >
            <View
                style={[
                    stylesheet.infoContainer,
                    {
                        backgroundColor: colors.content.container.subtle,
                        borderRadius: radiuses.sm,
                        padding: spaces.spacingMd,
                        gap: spaces.spacingSm
                    }
                ]}
            >
                {
                    detail.imageUrl ?
                        <Image
                            style={[
                                stylesheet.notificationImage,
                                {
                                    borderRadius: radiuses.sm
                                }
                            ]}
                            source={{
                                uri: detail.imageUrl
                            }}
                            resizeMode="cover"
                        />
                    :
                        null
                }
                <Text
                    customColor={colors.content.text.high}
                    variant="titleMediumSize"
                >
                    {detail.title}
                </Text>
                <Text
                    customColor={colors.content.text.mid}
                    variant="bodyMediumSize"
                >
                    {detail.description}
                </Text>
                <View
                    style={[
                        stylesheet.statsContainer,
                        {
                            paddingVertical: spaces.spacingSm
                        }
                    ]}
                >
                    <Text
                        customColor={colors.content.text.mid}
                        variant="bodySmallSize"
                    >
                        {`${localize("notifications-table-date")}: ${moment(detail.createdAt).format("DD.MM.YYYY HH:mm")}`}
                    </Text>
                    <Text
                        customColor={colors.content.text.mid}
                        variant="bodySmallSize"
                    >
                        {`${localize("notifications-table-target")}: ${detail.sendToAll ? localize("notifications-table-all-users") : `${detail.targetCount} ${localize("notifications-table-person-count")}`}`}
                    </Text>
                </View>
                <View
                    style={[
                        stylesheet.statsContainer,
                        {
                            paddingVertical: spaces.spacingSm
                        }
                    ]}
                >
                    <Text
                        customColor={colors.content.text.high}
                        variant="bodyMediumSize"
                    >
                        {`${localize("notifications-table-read-rate")}: ${detail.readCount} / ${detail.targetCount}`}
                    </Text>
                    <Text
                        customColor={colors.content.text.high}
                        variant="bodyMediumSize"
                    >
                        %{readRate.toFixed(1)}
                    </Text>
                </View>
                <View
                    style={[
                        stylesheet.statBarTrack,
                        {
                            backgroundColor: colors.content.border.subtle,
                            borderRadius: radiuses.full,
                            marginTop: spaces.spacingXs,
                            height: spaces.spacingXs
                        }
                    ]}
                >
                    <View
                        style={[
                            stylesheet.statBarFill,
                            {
                                backgroundColor: colors.content.text.high,
                                borderRadius: radiuses.full,
                                width: `${readRate}%`
                            }
                        ]}
                    />
                </View>
            </View>
            <View
                style={[
                    stylesheet.sectionTitle,
                    {
                        marginBottom: spaces.spacingSm,
                        marginTop: spaces.spacingMd
                    }
                ]}
            >
                <Text
                    customColor={colors.content.text.high}
                    variant="titleSmallSize"
                >
                    {`${localize("notifications-detail-read-users-title")} (${detail.readUsers ? detail.readUsers.length : 0})`}
                </Text>
            </View>
            {
                detail.readUsers && detail.readUsers.length > 0 ?
                    <View>
                        <View
                            style={[
                                stylesheet.tableHeaderRow,
                                {
                                    borderBottomColor: colors.content.border.subtle,
                                    borderBottomWidth: borders.seperator,
                                    paddingVertical: spaces.spacingSm
                                }
                            ]}
                        >
                            <View
                                style={stylesheet.userCell}
                            >
                                <Text
                                    customColor={colors.content.text.mid}
                                    variant="bodySmallSize"
                                >
                                    {localize("notifications-detail-table-user")}
                                </Text>
                            </View>
                            <View
                                style={stylesheet.mailCell}
                            >
                                <Text
                                    customColor={colors.content.text.mid}
                                    variant="bodySmallSize"
                                >
                                    {localize("notifications-detail-table-mail")}
                                </Text>
                            </View>
                            <View
                                style={stylesheet.dateCell}
                            >
                                <Text
                                    customColor={colors.content.text.mid}
                                    variant="bodySmallSize"
                                >
                                    {localize("notifications-detail-table-read-time")}
                                </Text>
                            </View>
                        </View>
                        {detail.readUsers.map((item, index) => {
                            const user = item.user;
                            const userName = user?.fullName || `${user?.firstName || ""} ${user?.lastName || ""}`.trim() || "-";

                            return <View
                                key={item.readAt ? `${item.readAt}-${index}` : `${index}`}
                                style={[
                                    stylesheet.tableDataRow,
                                    {
                                        borderBottomColor: colors.content.border.subtle,
                                        borderBottomWidth: borders.seperator,
                                        paddingVertical: spaces.spacingMd
                                    }
                                ]}
                            >
                                <View
                                    style={stylesheet.userCell}
                                >
                                    <Text
                                        customColor={colors.content.text.high}
                                        variant="bodySmallSize"
                                        numberOfLines={1}
                                    >
                                        {userName}
                                    </Text>
                                </View>
                                <View
                                    style={stylesheet.mailCell}
                                >
                                    <Text
                                        customColor={colors.content.text.mid}
                                        variant="bodySmallSize"
                                        numberOfLines={1}
                                    >
                                        {user?.mail || "-"}
                                    </Text>
                                </View>
                                <View
                                    style={stylesheet.dateCell}
                                >
                                    <Text
                                        customColor={colors.content.text.mid}
                                        variant="bodySmallSize"
                                    >
                                        {moment(item.readAt).format("DD.MM.YYYY HH:mm")}
                                    </Text>
                                </View>
                            </View>;
                        })}
                    </View>
                :
                    <View
                        style={[
                            stylesheet.emptyStateContainer,
                            {
                                padding: spaces.spacingLg
                            }
                        ]}
                    >
                        <Text
                            customColor={colors.content.text.mid}
                            variant="bodyMediumSize"
                        >
                            {localize("notifications-detail-no-readers")}
                        </Text>
                    </View>
            }
        </ScrollView>;
    };

    return <Modal
        id="notification-detail-modal"
        portalName="modal-system"
        isActive={isVisible}
        onClosed={onClose}
    >
        <View
            style={[
                stylesheet.container,
                {
                    backgroundColor: colors.project.brandWhite,
                    borderRadius: radiuses.lg,
                    padding: spaces.spacingXl
                }
            ]}
        >
            <View
                style={[
                    stylesheet.headerContainer,
                    {
                        marginBottom: spaces.spacingLg
                    }
                ]}
            >
                <Text
                    customColor={colors.content.text.high}
                    variant="titleLargeSize"
                >
                    {localize("notifications-detail-title")}
                </Text>
            </View>
            {renderContent()}
            <View
                style={[
                    stylesheet.footerContainer,
                    {
                        marginTop: spaces.spacingMd
                    }
                ]}
            >
                <Button
                    title={localize("notifications-detail-close")}
                    customTextColor={colors.project.brandWhite}
                    customBorderColor="secondaryButton"
                    customColor="secondaryButton"
                    spreadBehaviour="free"
                    onPress={onClose}
                    variant="filled"
                />
            </View>
        </View>
    </Modal>;
};
export default NotificationDetailModal;
